import { takeAvatars } from '@components/ArtBoard/Threads'
import { getAuthorName } from '@components/ArtBoard/Threads/components/CommentHeader/utils'
import type { ID } from '@octopus-do/octopus-project-core'
import type { Thread } from '@store/reducers/threads'

import type { Comment, Recipient } from '@/types'
import type { CommentsItemKind, ThreadListItem } from './types'

const UNTITLED: Record<Exclude<CommentsItemKind, 'thread'>, string> = {
  block: 'Untitled block',
  page: 'Untitled page',
}

const latestOf = (first: number, comments: Comment[]) =>
  comments.reduce((max, c) => Math.max(max, c.createdAt), first)

const buildItem = (
  thread: Thread,
  selected: ID | null,
  comments: Record<ID, Comment[]>,
  users: Record<ID, Recipient>,
  unread: Set<ID>
): ThreadListItem => {
  const {
    uuid: id,
    owner,
    createdAt,
    resolved,
    content,
    author: storedName,
  } = thread
  const replies = comments[id] || []
  const active = selected === id

  const author = owner ? users[owner] : null

  return {
    id,
    kind: 'thread',
    pageId: null,
    commentators: takeAvatars(owner, replies, users),
    active,
    createdAt: latestOf(createdAt, replies),
    repliesCount: replies.length,
    name: getAuthorName(author, storedName),
    resolved,
    content,
    unread: unread.has(id),
  }
}

export const buildThreadsList = (
  selected: ID | null,
  threads: Thread[],
  comments: Record<ID, Comment[]>,
  users: Record<ID, Recipient>,
  unread: Set<ID> = new Set()
) => threads.map((t) => buildItem(t, selected, comments, users, unread))

export interface CommentablePlace {
  id: ID
  kind: Exclude<CommentsItemKind, 'thread'>
  title: string
  pageId: ID
  comments: Comment[]
}

// A block or a page with comments: named after the place, signed by the
// people who wrote there, showing the newest comment as its preview
export const buildCommentableItems = (
  places: CommentablePlace[],
  users: Record<ID, Recipient>,
  unread: Set<ID>,
  open: ID[]
): ThreadListItem[] =>
  places
    .filter(({ comments }) => comments.length > 0)
    .map(({ id, kind, title, pageId, comments }) => {
      const [first, ...rest] = comments
      const last = comments[comments.length - 1]

      return {
        id,
        kind,
        pageId,
        commentators: takeAvatars(first.owner || null, rest, users),
        active: open.includes(id),
        createdAt: latestOf(first.createdAt, rest),
        repliesCount: comments.length,
        name: title || UNTITLED[kind],
        resolved: false,
        content: last.content,
        unread: unread.has(id),
      }
    })
